var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
/**
 * Copyright 2021, SumUp Ltd.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import styled from '../../styles/styled';
import { useAnimation } from '../../hooks/useAnimation';
import Body from '../Body';
import CloseButton from '../CloseButton';
import { createUseToast } from '../ToastContext';
import { hideVisually } from '../../styles/style-mixins';
import { deprecate } from '../../util/logger';
import { DEPRECATED_VARIANTS, NOTIFICATION_COLORS, NOTIFICATION_ICONS, } from '../Notification/constants';
const TRANSITION_DURATION = 200;
const DEFAULT_HEIGHT = 'auto';
const toastWrapperStyles = ({ theme, variant, }) => css `
  background-color: var(--cui-bg-elevated);
  border-radius: ${theme.borderRadius.byte};
  border: ${theme.borderWidth.mega} solid
    var(${NOTIFICATION_COLORS[variant].border});
  overflow: hidden;
  will-change: height;
  transition: opacity ${TRANSITION_DURATION}ms ease-in-out,
    height ${TRANSITION_DURATION}ms ease-in-out,
    visibility ${TRANSITION_DURATION}ms ease-in-out;
`;
const NotificationToastWrapper = styled('div')(toastWrapperStyles);
const contentWrapperStyles = ({ theme }) => css `
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: ${theme.spacings.kilo} ${theme.spacings.mega};
`;
const ContentWrapper = styled('div')(contentWrapperStyles);
const contentStyles = ({ theme }) => css `
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding-right: ${theme.spacings.peta};
  padding-left: ${theme.spacings.mega};
`;
const Content = styled('div')(contentStyles);
const IconWrapper = styled.div(({ variant }) => css `
      position: relative;
      align-self: flex-start;
      flex-grow: 0;
      flex-shrink: 0;
      line-height: 0;
      color: var(${NOTIFICATION_COLORS[variant].fg});
    `);
const closeButtonStyles = ({ theme }) => css `
  flex-grow: 0;
  flex-shrink: 0;
  align-self: flex-start;
  margin-top: -${theme.spacings.bit};
  margin-bottom: -${theme.spacings.bit};
  margin-left: auto;
`;
const StyledCloseButton = styled(CloseButton)(closeButtonStyles);
export function NotificationToast(_a) {
    var { variant = 'info', body, headline, onClose, iconLabel = '', isVisible, tracking, duration } = _a, // this is the auto-dismiss duration, not the animation duration. We shouldn't pass it to the wrapper along with ...props
    props = __rest(_a, ["variant", "body", "headline", "onClose", "iconLabel", "isVisible", "tracking", "duration"]);
    if (process.env.NODE_ENV !== 'production') {
        if (DEPRECATED_VARIANTS[variant]) {
            deprecate('NotificationToast', `The "${variant}" variant has been deprecated. Use "${DEPRECATED_VARIANTS[variant]}" instead.`);
        }
    }
    const contentElement = useRef(null);
    const [isOpen, setOpen] = useState(false);
    const [height, setHeight] = useState(getHeight(contentElement));
    const [, setAnimating] = useAnimation();
    useEffect(() => {
        setAnimating({
            duration: TRANSITION_DURATION,
            onStart: () => {
                setHeight(getHeight(contentElement));
                // Delaying the state update until the next animation frame ensures that
                // the browsers renders the new height before the animation starts.
                window.requestAnimationFrame(() => {
                    setOpen(isVisible);
                });
            },
            onEnd: () => {
                setHeight(DEFAULT_HEIGHT);
            },
        });
    }, [isVisible, setAnimating]);
    const Icon = NOTIFICATION_ICONS[variant];
    return (_jsx(NotificationToastWrapper, Object.assign({ ref: contentElement, style: {
            opacity: isOpen ? 1 : 0,
            height: isOpen ? height : 0,
            visibility: isOpen ? 'visible' : 'hidden',
        }, variant: variant }, props, { children: _jsxs(ContentWrapper, { children: [_jsx(IconWrapper, Object.assign({ variant: variant }, { children: _jsx(Icon, { role: "presentation" }) })), _jsx("span", Object.assign({ css: hideVisually }, { children: iconLabel })), _jsxs(Content, { children: [headline && (_jsx(Body, Object.assign({ variant: 'highlight', as: "h3" }, { children: headline }))), _jsx(Body, { children: body })] }), _jsx(StyledCloseButton, { label: "-" // We need to pass a label here to prevent CloseButton from throwing an error
                    , "aria-hidden": "true", size: "kilo", onClick: onClose, tracking: tracking
                        ? Object.assign({ component: 'notification-close' }, tracking) : undefined, tabIndex: -1 })] }) })));
}
NotificationToast.TRANSITION_DURATION = TRANSITION_DURATION;
export function getHeight(element) {
    if (!element || !element.current) {
        return DEFAULT_HEIGHT;
    }
    return `${element.current.scrollHeight}px`;
}
export const useNotificationToast = createUseToast(NotificationToast);
